import { getPadronesMap } from 'api/recaudacion/utils';
import { store } from 'store';
import {
  CURP_PATTERN,
  digitoVerificador,
  INE_PATTERN,
  RFC_PATTERN,
  EMAIL_PATTERN,
  digitoVerificadorRFC,
} from 'utils/patterns';
import { normalizeText } from 'utils/normalizers';

const getPadronLabel = (tipoDePadron) => store.getState().catalogs
  .padrones.find((e) => e.id === tipoDePadron)?.descripcion;

export const curpsGenericas = ['XEXX010101HNEXXXA4', 'XEXX010101MNEXXXA8'];
export const rfcGenerico = 'XEXX010101000';

export const generateCURPRules = ({
  validateUnique = false,
  tipoDePadron,
  required = true,
  getter = null,
  label = 'registro',
  id: _id,
  callback,
}) => ([
  { required: Boolean(required) },
  ({ getFieldValue }) => ({
    validator: async (rule, value) => {
      if (value) {
        if (tipoDePadron === 15 && getFieldValue('tipo_de_persona') === 3 && curpsGenericas.includes(value)) return;
        const validado = value.toUpperCase().match(CURP_PATTERN);
        if (!validado) {
          throw new Error('Ingrese una CURP válida.');
        }
        if (validado[2] !== digitoVerificador(validado[1])) {
          throw new Error('Dígito verificador incorrecto');
        }
        if (validateUnique) {
          const id = _id || getFieldValue('id');
          const propsNames = { 1: 'CURP', 15: 'curp' };
          const propName = getter ? rule.field : propsNames[tipoDePadron];
          const params = { [propName]: value };
          const matches = await (getter || getPadronesMap[tipoDePadron])(params);
          const match = matches?.results.filter((e) => e.id !== id)
            .find((e) => e?.[propName] === value);
          if (match) {
            if (callback) {
              callback(match);
            } else {
              throw new Error(`Ya existe un ${getPadronLabel(tipoDePadron) || label} - ${match?.clave || match?.clave_ciudadana} con esta CURP.`);
            }
          }
        }
      }
    },
  }),
]);

export const generateINERules = ({
  validateUnique = false,
  required = true,
  id: _id,
}) => ([
  { required: Boolean(required) },
  ({ getFieldValue }) => ({
    validator: async (_, val) => {
      if (val) {
        const invalid = !INE_PATTERN.test(val);
        if (validateUnique && !invalid) {
          const id = _id || getFieldValue('id');
          const matches = await getPadronesMap[1]({ INE: val });
          const [match] = matches.results.filter((e) => e.id !== id);
          if (match) {
            throw new Error(`Ya existe un ${getPadronLabel(1)} con esta INE.`);
          }
        } else if (invalid) {
          throw new Error('Ingrese una INE válida.');
        }
      }
    },
  }),
]);

export const generateRFCRules = ({
  validateUnique = false,
  tipoDePersona: _tipoDePersona,
  tipoDePersonaName,
  tipoDePadron,
  required = false,
  getter = null,
  label = 'registro',
  id: _id,
}) => ([
  ({ getFieldValue }) => ({
    validator: async (rule, _value) => {
      if (required && !_value) throw new Error('El campo es requerido');
      if (!_value) return;
      const tipoDePersona = tipoDePersonaName
        ? getFieldValue(tipoDePersonaName)
        : _tipoDePersona || getFieldValue('tipo_de_persona');
      if (tipoDePadron === 15 && tipoDePersona === 3 && _value === rfcGenerico) return;
      const value = _value.toString().toUpperCase();
      const lengthsByTipoPersona = {
        1: 13, // fisica
        2: 12, // moral
        3: 13, // extranjero
      };
      if (value.length !== lengthsByTipoPersona[tipoDePersona]) throw new Error('Longitud inválida');
      if (!RFC_PATTERN.test(value)) throw new Error('RFC inválido');
      if (validateUnique) {
        const id = _id || getFieldValue('id');
        const propsNames = { 1: 'RFC', 15: 'rfc' };
        const propName = getter ? rule.field : propsNames[tipoDePadron];
        const params = { [propName]: value };
        const matches = await (getter || getPadronesMap[tipoDePadron])(params);
        const [match] = (matches.results || matches).filter((e) => e.id !== id);
        if (match) {
          throw new Error(`Ya existe un ${getPadronLabel(tipoDePadron) || label} con este RFC.`);
        }
      }
    },
  }),
  ({ getFieldValue }) => ({
    validator: async (_, _value) => {
      if (!_value) return;
      const tipoDePersona = tipoDePersonaName ? getFieldValue(tipoDePersonaName)
        : _tipoDePersona || getFieldValue('tipo_de_persona');
      if (tipoDePadron === 15 && tipoDePersona === 3 && _value === rfcGenerico) return;
      const value = _value.toString().toUpperCase();
      const lengthsByTipoPersona = {
        1: 13, // fisica
        2: 12, // moral
        3: 13, // extranjero
      };
      if (value.length !== lengthsByTipoPersona[tipoDePersona]) return;
      if (!RFC_PATTERN.test(value)) return;
      const _digitoVerificador = value.at(-1);
      const digitoVerificadorCalculado = digitoVerificadorRFC(value.substring(0, value.length - 1));
      if (digitoVerificadorCalculado !== _digitoVerificador) {
        throw new Error(`Dígito verificador calculado es ${digitoVerificadorCalculado}. Si el valor introducido (${_digitoVerificador}) es correcto puede continuar`);
      }
    },
    warningOnly: true,
  }),
]);

export const generateEmailRules = ({
  validateUnique = false,
  tipoDePadron,
  required = false,
  getter = null,
  label = 'registro',
  id: _id,
}) => ([
  { required },
  ({ getFieldValue }) => ({
    validator: async (rule, value) => {
      if (value) {
        const invalid = !EMAIL_PATTERN.test(value);
        if (validateUnique && !invalid) {
          const id = _id || getFieldValue('id');
          const propName = rule.field;
          const params = { [propName]: value };
          const matches = await (getter || getPadronesMap[tipoDePadron])(params);
          const [match] = (matches.results || matches).filter((e) => e.id !== id);
          if (match) {
            throw new Error(`Ya existe un ${getPadronLabel(tipoDePadron) || label} con este correo electrónico.`);
          }
        } else if (invalid) {
          throw new Error('Ingrese un correo eletrónico válido.');
        }
      }
    },
  }),
]);

export const generatePhoneRules = ({
  validateUnique = false,
  tipoDePadron,
  required = false,
  getter = null,
  label = 'registro',
  id: _id,
}) => ([
  { required: Boolean(required) },
  ({ getFieldValue }) => ({
    validator: async (rule, value) => {
      const invalid = !/^[0-9]{10}$/.test(value);
      if (validateUnique && !invalid) {
        const id = _id || getFieldValue('id');
        const propName = rule.field;
        const params = { [propName]: value };
        const matches = await (getter || getPadronesMap[tipoDePadron])(params);
        const [match] = (matches.results || matches).filter((e) => e.id !== id);
        if (match) {
          throw new Error(`Ya existe un ${getPadronLabel(tipoDePadron) || label} con este número de celular.`);
        }
      }
    },
  }),
]);

export const uniqueValidator = (data = [], current, label) => ({
  validator: async (rule, value) => {
    const exists = data.some((item) => item[rule.field] === value && item.id !== current);
    if (exists) {
      throw new Error(`${label || rule.field} debe tener un valor único`);
    }
  },
});

export const customUniqueValidator = ({
  getter,
  fieldName,
  tipoDePadron,
  label,
  preValidation,
  postValidation,
  postValidationCond = () => true,
  errorMessage,
}) => (formInstance) => ({
  validator: async (rule, value) => {
    if (!value) return;
    const name = fieldName || rule.field;
    if (preValidation) await preValidation({ formInstance });
    const params = { [name]: value };
    const matches = await (getter || getPadronesMap[tipoDePadron])(params);
    const match = (matches.results || matches)
      .find((e) => normalizeText(e[name]) === normalizeText(value) && formInstance.getFieldValue('id') !== e.id);
    if (postValidation && postValidationCond(formInstance)) {
      await postValidation({ field: name, formInstance, match });
    }
    if (match) {
      throw new Error(errorMessage || `Ya existe un ${label || getPadronLabel(tipoDePadron) || 'registro'} con ${name} ingresado`);
    }
  },
});

export const percentajeValidator = () => ({
  validator: async (_, val) => {
    const parsedVal = parseFloat(val);
    if (val) {
      if (parsedVal > 100) {
        throw new Error('El valor no puede ser mayor a 100');
      } else if (parsedVal <= 0) {
        throw new Error('El valor debe ser mayor a 0');
      }
    }
  },
});
